/*
 *   File : swp-list.js
 *   Author : https://evoqins.com
 *   Description : SWP list (Systematic Plans)
 *   Integrations : NA
 *   Version : 1.0.0
*/

import React, { useEffect, useRef, useState } from 'react';
import { format } from 'date-fns';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Modal } from 'bootstrap/dist/js/bootstrap.min';

import { Icon } from '../../Components/Icon';
import { CustomDayPicker, CustomSearchInput, CustomSelectBox, DateRangePicker } from '../../Components/FormElements';
import { PrimaryButton, SecondaryButton, TextButton } from '../../Components/Buttons';
import { CustomDataTable } from '../../Components/DataTable';
import { EmptyScreen, Loader } from '../../Components/Others';
import { SystematicPlanModal } from '../../Components/Modal';
import { APIService } from '../../Service';
import { getSwpColumns } from './swp-table-config';

const PAGE_SIZE = 10;

const SWP = ({ customerId = null, embedded = false } = {}) => {
    const scrollRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();
    const PROFILE_DATA = useSelector((store) => store.Reducer.PROFILE_DATA);
    const hasSwpDownloadAccess = PROFILE_DATA?.modules?.some((module) => {
        const name = module?.name?.toLowerCase() || '';
        return name === 'swp download' || name === 'sip download';
    });

    const [searchValue, setSearchValue] = useState('');
    const [toggleFilter, setToggleFilter] = useState(false);

    const [rayiStatus, setRayiStatus] = useState([]);
    const [fpStatus, setFpStatus] = useState([]);
    const [registeredDate, setRegisteredDate] = useState(null);
    const [selectedScheme, setSelectedScheme] = useState(null);
    const [selectedDay, setSelectedDay] = useState(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [swpData, setSwpData] = useState({});
    const [filterData, setFilterData] = useState({});
    const [filterLoader, setFilterLoader] = useState(true);
    const [isRendered, setIsRendered] = useState(true);
    const [loader, setLoader] = useState(true);
    const [downloadLoader, setDownloadLoader] = useState(false);
    const [selectedSwp, setSelectedSwp] = useState(null);

    useEffect(() => {
        if (!embedded) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, []);

    useEffect(() => {
        if (selectedSwp !== null) {
            const modalEl = document.getElementById('systematic-plan-modal');
            if (!modalEl) return;
            const swpDetailModal = new Modal(modalEl);
            swpDetailModal.show();
        }
    }, [selectedSwp]);

    // Deferred so the Strict Mode double mount results in a single request
    useEffect(() => {
        const timer = setTimeout(() => {
            _filterData();
        }, 0);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        if (isRendered !== true) return;

        const timer = setTimeout(() => {
            _getSwps();
            setIsRendered(false);
        }, 0);
        return () => clearTimeout(timer);
    }, [isRendered]);

    const _onSearch = () => {
        setIsRendered(true);
        setCurrentPage(1);
    };

    const _handleSearchClear = () => {
        setIsRendered(true);
        setSearchValue('');
        setCurrentPage(1);
    };

    const _handleToggleFilter = () => {
        setToggleFilter((prev) => !prev);
    };

    const _handleClear = () => {
        setSelectedDay(null);
        setRegisteredDate(null);

        setFpStatus(filterData.fp_status[0])
        setRayiStatus(filterData.rayi_status[0])
        setSelectedScheme(filterData.investment_type[0]);

        setIsRendered(true);
        setCurrentPage(1);
    };

    const _handleFilter = () => {
        setIsRendered(true);
        setCurrentPage(1);
    };

    const _handleTablePageChange = (page_num) => {
        const nextPage = Math.max(1, Number(page_num) || 1);
        setCurrentPage(nextPage);
        if (scrollRef.current) {
            scrollRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        setIsRendered(true);
    };

    const _handleViewDetails = (row) => {
        setSelectedSwp(row);
    };

    const _handleOrderHistory = (row) => {
        const mfId = row?.mf_id ?? row?.scheme_id ?? row?.from_mf_id ?? row?.from_scheme_id ?? null;
        const scheme = row?.scheme || row?.from_scheme || "";

        if (customerId) {
            navigate(location.pathname.includes('support') ? `/support/${customerId}` : `/customers/${customerId}`, {
                state: {
                    ...location.state,
                    inner_tab_index: 2,
                    order_tab: 8,
                    mf_id: mfId,
                    scheme,
                },
            });
            return;
        }

        navigate('/other-transactions', {
            state: {
                tab_index: 2,
                current_page: 1,
                rayi_customer_id: row?.rayi_customer_id || "",
                mf_id: mfId,
                scheme,
            },
        });
    };

    const SWP_DATA_COLUMN = getSwpColumns({
        onViewDetails: _handleViewDetails,
        includeCustomerColumns: !customerId,
    });

    // API - Filter data
    const _filterData = () => {
        let is_post = false;
        let url = 'swp/swp-filter-data';
        APIService(is_post, url).then((response) => {
            if (response.status_code === 200) {
                setFilterData(response.data);

                setFpStatus(response.data.fp_status[0])
                setRayiStatus(response.data.rayi_status[0])
                setSelectedScheme(response.data.investment_type[0]);
            } else {
                setFilterData([]);
            }
            setFilterLoader(false);
        });
    };

    // API - Get SWP list
    const _getSwps = () => {

        let updated_rayi_status = [];
        let updated_fp_status = [];

        if (rayiStatus?.value !== null) {
            updated_rayi_status = rayiStatus.filter(item => item?.value != null).map((value) => {
                return value.value
            })
        }

        if (fpStatus?.value !== null) {
            updated_fp_status = fpStatus.filter(item => item?.value != null).map((value) => {
                return value.value
            });
        }

        const registered_start_date = registeredDate === null ? null : format(new Date(registeredDate[0]), 'dd-MM-yyyy');
        const registered_end_date = registeredDate === null ? null : format(new Date(registeredDate[1]), 'dd-MM-yyyy');

        let is_post = true;
        let url = 'swp/list';
        let request = {
            "query": searchValue,
            "page_num": currentPage,
            "page_size": PAGE_SIZE,
            "rayi_status": updated_rayi_status,
            "fp_status": updated_fp_status,
            "investment_type": selectedScheme?.value,
            "registered_start_date": registered_start_date,
            "registered_end_date": registered_end_date,
            "swp_day": selectedDay,
            ...(customerId ? { "customer_id": customerId } : {}),
        }

        setLoader(true);
        APIService(is_post, url, request).then((response) => {
            if (response.status_code === 200) {

                const updated_data = (response.data.swps || []).map((item, index) => ({
                    ...item,
                    _index: index
                }));

                setSwpData({
                    ...response.data,
                    swps: updated_data
                });

            } else {
                setSwpData({ swps: [] });
            }
            setLoader(false);
            setIsRendered(false);
        })
    };

    // API - Swp list download
    const _swpDownload = () => {
        setDownloadLoader(true);

        let updated_rayi_status = [];
        let updated_fp_status = [];

        if (rayiStatus?.value !== null) {
            updated_rayi_status = rayiStatus.filter(item => item?.value != null).map((value) => {
                return value.value
            })
        }

        if (fpStatus?.value !== null) {
            updated_fp_status = fpStatus.filter(item => item?.value != null).map((value) => {
                return value.value
            });
        }

        const registered_start_date = registeredDate === null ? null : format(new Date(registeredDate[0]), 'dd-MM-yyyy');
        const registered_end_date = registeredDate === null ? null : format(new Date(registeredDate[1]), 'dd-MM-yyyy');

        let is_post = true;
        let url = 'swp/swp-download';
        let request = {
            "rayi_status": updated_rayi_status,
            "fp_status": updated_fp_status,
            "investment_type": selectedScheme?.value,
            "query": searchValue,
            "registered_start_date": registered_start_date,
            "registered_end_date": registered_end_date,
            "swp_day": selectedDay,
            ...(customerId ? { "customer_id": customerId } : {}),
        }

        APIService(is_post, url, request).then((response) => {
            if (response.status_code === 200) {
                const url = response.data.url;
                const link = document.createElement('a');
                link.href = url;
                link.download = ''; // Optional: specify a file name
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else {
                toast.dismiss();
                toast.success(response.message, { type: "error" })
            }
            setDownloadLoader(false);
        })
    };

    const hasRows = Array.isArray(swpData?.swps) && swpData.swps.length > 0;

    const listContent = (
        <>
            <div className={'padding-32px-lr padding-32px-top'}>
                <h6 className='mb-0 e-text-smoky-black e-primary-font e-font-16 e-font-weight-600 line-height-20px'>
                    SWP summary
                </h6>

                <div className='row pt-3'>
                    <div className='col-3'>
                        <div className='e-bg-lavender-dark border-radius-4 p-3 d-flex flex-column h-100 gap-2 justify-content-between'>
                            <p className='mb-0 e-text-dark-silver e-primary-font e-font-14 e-font-weight-400 line-height-16px'>
                                No of Active SWP's
                            </p>
                            {loader ? (
                                <span className="e-summary-value-skeleton"></span>
                            ) : (
                                <p className='mb-0 e-text-chinese-black e-primary-font e-font-14 e-font-weight-600 line-height-20px'>
                                    {swpData?.total_active_swp_count ?? '-'}
                                </p>
                            )}
                        </div>
                    </div>
                    <div className='col-3'>
                        <div className='e-bg-lavender-dark border-radius-4 p-3 d-flex flex-column h-100 gap-2 justify-content-between'>
                            <p className='mb-0 e-text-dark-silver e-primary-font e-font-14 e-font-weight-400 line-height-16px'>
                                Total SWP amount
                            </p>
                            {loader ? (
                                <span className="e-summary-value-skeleton"></span>
                            ) : (
                                <p className='mb-0 e-text-chinese-black e-primary-font e-font-14 e-font-weight-600 line-height-20px'>
                                    {swpData?.total_swp_amount ?? '-'}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className='d-flex align-items-center pb-2 justify-content-end gap-16px padding-32px-lr pt-3'>
                {!embedded && hasSwpDownloadAccess && (
                    <SecondaryButton
                        name="Download List"
                        disabled={loader || !hasRows}
                        className={downloadLoader ? 'w-129px h-42px' : ''}
                        loader={downloadLoader}
                        onClick={_swpDownload}
                    />
                )}

                {!embedded &&
                    <CustomSearchInput
                        placeholder="Search with customer ID or name"
                        type="text"
                        value={searchValue}
                        onSearch={() => _onSearch()}
                        onClear={_handleSearchClear}
                        className="w-370px"
                        onSearchClear={() => _handleSearchClear()}
                        onKeyPress={() => _onSearch()}
                        handleChange={(e) => {
                            setSearchValue(e);
                            if (e.length === 0) {
                                _handleSearchClear();
                            }
                        }}
                    />
                }

                <div
                    className={`${toggleFilter ? 'e-bg-blue-violet' : ''} padding-10px-all border-radius-8 e-cursor-pointer e-border-1px-light-purple cursor-pointer`}
                    onClick={_handleToggleFilter}>
                    <Icon
                        icon='filter'
                        size='1.25rem'
                        className="d-flex"
                        color={toggleFilter && '#FFFFFF'}
                    />
                </div>
            </div>

            <div className={`e-filter-content ${toggleFilter ? 'expanded ' : 'e-overflow collapsed'}`}>
                <div className='e-bg-main-white pt-3 pb-4 padding-32px-lr'>
                    <div className='d-flex justify-content-end'>
                        <TextButton name="Clear all" onClick={_handleClear} />
                    </div>
                    <div className='d-flex gap-4 padding-32px-bottom flex-wrap'>
                        <CustomSelectBox
                            title={embedded ? "Rayi Status" : "Rayi SWP Status"}
                            placeholder='Select'
                            controlWidth='9.5rem'
                            options={filterData.rayi_status}
                            loader={filterLoader}
                            isMulti={true}
                            type={3}
                            isSearchable={false}
                            value={rayiStatus}
                            onChange={(value) => setRayiStatus(value)}
                        />

                        <CustomSelectBox
                            title={embedded ? "FP Status" : "FP SWP Status"}
                            placeholder='Select'
                            controlWidth='9.5rem'
                            options={filterData.fp_status}
                            loader={filterLoader}
                            isSearchable={false}
                            isMulti={true}
                            type={3}
                            value={fpStatus}
                            onChange={(value) => setFpStatus(value)}
                        />

                        <DateRangePicker
                            label="Registered date"
                            placeholder="Select"
                            calendarWrapperClass="end-0"
                            maxDate={new Date()}
                            value={registeredDate}
                            handleClear={() => setRegisteredDate(null)}
                            handleChange={(date) => setRegisteredDate(date)}
                        />

                        <CustomSelectBox
                            title={"Scheme name"}
                            placeholder='Select'
                            controlWidth='9rem'
                            loader={filterLoader}
                            options={filterData.investment_type}
                            isSearchable={false}
                            value={selectedScheme}
                            onChange={(value) => setSelectedScheme(value)}
                        />

                        <CustomDayPicker
                            label="SWP day"
                            placeholder="Select"
                            selected={selectedDay}
                            handleClear={() => setSelectedDay(null)}
                            handleSelect={(day) => setSelectedDay(day)}
                        />
                    </div>

                    <PrimaryButton
                        name='Filter now'
                        paddingBlock='padding-12px-tb padding-40px-lr'
                        onClick={_handleFilter}
                    />
                </div>
            </div>

            {loader ? (
                <Loader className={embedded ? "pt-5 min-vh-50" : "pt-5 min-vh-75"} />
            ) : (
                <>
                    {hasRows ? (
                        <>
                            <div className='pt-2 padding-32px-lr d-flex gap-1 align-items-center justify-content-end'>
                                <div>
                                    <img
                                        src={require('../../Assets/Image/scroll-animation.gif')}
                                        draggable={false}
                                        className='e-rotate-270'
                                        width={24}
                                        height={24}
                                        alt='scroll'
                                    />
                                </div>
                                <p className='mb-0 e-text-bleu-de-france e-alt-font e-font-12 e-font-weight-500 line-height-14px'>
                                    Scroll horizontally for view more information
                                </p>
                            </div>

                            <div className={`e-data-table-wrapper-striped e-data-table-no-expander ${embedded ? 'pt-3 pb-4' : 'pb-1'}`}>
                                <CustomDataTable
                                    columns={SWP_DATA_COLUMN}
                                    data={swpData.swps}
                                    striped
                                    pagination={true}
                                    paginationPerPage={PAGE_SIZE}
                                    paginationDefaultPage={swpData.current_page || currentPage}
                                    paginationTotalRows={swpData.total_count}
                                    lastPage={swpData.total_page}
                                    onChangePage={_handleTablePageChange}
                                />
                            </div>
                        </>
                    ) : (
                        <EmptyScreen
                            width={120}
                            height={120}
                            className={embedded ? "min-vh-50" : "min-vh-75"}
                            image={require('../../Assets/Image/NotFound/no-data.png')}
                            title='No data found'
                        />
                    )}
                </>
            )}
        </>
    );

    return (
        <>
            {embedded ?
                <div ref={scrollRef}>
                    {listContent}
                </div>
                :
                <section className='e-section-height pb-5'>
                    <div className='e-bg-lavender padding-52px-lr pt-2 padding-112px-bottom'>
                        <h3 className='mb-0 e-text-chinese-black e-primary-font e-font-24 e-font-weight-600 line-height-24px'>
                            SWP
                        </h3>
                    </div>

                    <div className='padding-52px-lr margin--80px-top'>
                        <div ref={scrollRef} className='e-bg-ghost-white border-radius-16 pb-4'>
                            {listContent}
                        </div>
                    </div>
                </section>
            }

            {selectedSwp && (
                <SystematicPlanModal data={selectedSwp || {}}
                    handleViewOrderHistory={_handleOrderHistory}
                    handleClose={() => {
                        setTimeout(() => {
                            setSelectedSwp(null);
                        }, 200);
                    }} />
            )}
        </>
    );
};

export default SWP;
